import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircleIcon, WarningCircleIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { Field, NumberInput, Select } from "@/components/CarFormFields";

/**
 * Compact car.gr export panel — only validation + mapping overrides.
 * The actual data fields (numplate, color types, interior type, owners,
 * youtube, circulation tax, etc.) live in the main "Τεχνικά Χαρακτηριστικά"
 * section so they are visible by default.
 */
export default function CarGrExportPanel({ form, set }) {
  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cargr-lookups/categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  const carId = form?.id;
  const refresh = useCallback(async () => {
    if (!carId) return;
    setLoading(true);
    try {
      const r = await api.get(`/cars/${carId}/cargr-preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error("Αποτυχία preview");
    } finally {
      setLoading(false);
    }
  }, [carId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const categoryOptions = useMemo(
    () => [
      { value: "0", label: "Auto (από αμάξωμα)" },
      ...categories.map((c) => ({ value: String(c.id), label: `${c.id} · ${c.name}` })),
    ],
    [categories]
  );

  const valid = preview?.valid;
  const featuresLen = preview?.features?.length || 0;
  const lastExported = preview?.last_exported_at
    ? new Date(preview.last_exported_at).toLocaleString("el-GR")
    : "—";

  let bannerColor = "bg-[#F9FAFB] border-black/10";
  if (valid === true) bannerColor = "bg-[#ECFDF5] border-[#A7F3D0]";
  else if (valid === false) bannerColor = "bg-[#FEF2F2] border-[#FECACA]";

  let statusLabel = "—";
  if (valid === true) statusLabel = "valid";
  else if (valid === false) statusLabel = "invalid";

  return (
    <div className="space-y-5" data-testid="cargr-export-panel">
      <div
        className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 border ${bannerColor}`}
      >
        <div className="flex items-center gap-3">
          {valid ? (
            <CheckCircleIcon size={28} weight="fill" className="text-[#16A34A]" />
          ) : (
            <WarningCircleIcon size={28} weight="fill" className="text-[#DC2626]" />
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">car.gr Export</div>
            <div className="text-sm font-medium">
              {valid ? "Έγκυρο για export — όλα τα required fields OK" : "Δεν είναι έγκυρο — λείπουν ή είναι λάθος πεδία"}
            </div>
            <div className="text-xs text-[#4B5563] mt-0.5 font-mono">
              Τελευταίο export: {lastExported} · Features mapped: {featuresLen}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="text-xs flex items-center gap-1.5 px-3 py-1.5 border border-black/15 bg-white hover:bg-black/5"
          data-testid="cargr-refresh-btn"
        >
          <ArrowsClockwiseIcon size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-[#F9FAFB] border border-black/10 p-4">
        <ReadOnly label="unique_id" value={preview?.unique_id || "—"} mono />
        <ReadOnly label="vtype" value={preview?.vtype || "car"} />
        <ReadOnly label="category_id" value={preview?.category_id || "—"} mono />
        <ReadOnly label="make_id" value={preview?.make_id || "—"} mono />
        <ReadOnly label="model_id" value={preview?.model_id || "—"} mono />
        <ReadOnly label="features" value={featuresLen} mono />
        <ReadOnly label="status" value={statusLabel} />
        <ReadOnly label="last_exported_at" value={lastExported} />
      </div>

      {preview && (preview.missing_fields?.length > 0 || preview.mapping_errors?.length > 0) && (
        <div className="border border-[#FECACA] bg-[#FEF2F2] p-4 space-y-2" data-testid="cargr-issues">
          {preview.missing_fields?.length > 0 && (
            <div className="text-xs">
              <span className="font-mono uppercase tracking-wider text-[#991B1B]">missing_fields:</span>{" "}
              <span className="font-mono">{preview.missing_fields.join(", ")}</span>
            </div>
          )}
          {preview.mapping_errors?.length > 0 && (
            <div className="text-xs">
              <span className="font-mono uppercase tracking-wider text-[#92400E]">mapping_errors:</span>
              <ul className="font-mono list-disc list-inside mt-1 space-y-0.5">
                {preview.mapping_errors.map((m) => (<li key={m} className="text-[#4B5563]">{m}</li>))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Συμπερίληψη στο car.gr feed">
          <label className="flex items-center gap-2 px-3 py-2 border border-black/20 bg-white cursor-pointer">
            <input
              type="checkbox"
              checked={form.include_in_car_gr_export !== false}
              onChange={(e) => set("include_in_car_gr_export", e.target.checked)}
              data-testid="cargr-include-checkbox"
            />
            <span className="text-sm">include_in_car_gr_export</span>
          </label>
        </Field>
        <Field label="Update interval (ημέρες)">
          <NumberInput
            value={form.car_gr_update_interval || 7}
            onChange={(e) => set("car_gr_update_interval", e.target.value)}
            min={1}
            max={365}
            data-testid="cargr-interval-input"
          />
        </Field>
        <Field label="Category override (manual)">
          <Select
            value={String(form.car_gr_category_id || 0)}
            onChange={(e) => set("car_gr_category_id", Number(e.target.value))}
            options={categoryOptions}
            data-testid="cargr-category-select"
          />
        </Field>
      </div>

      {preview && preview.mapping_errors?.some((m) => m.includes("make") || m.includes("model")) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#FEF3C7] border border-[#FCD34D] p-4">
          <Field label="make_id override (car.gr)">
            <NumberInput
              value={form.car_gr_make_id || 0}
              onChange={(e) => set("car_gr_make_id", Number(e.target.value) || 0)}
              placeholder="0 = auto"
              data-testid="cargr-make-id-input"
            />
          </Field>
          <Field label="model_id override (car.gr)">
            <NumberInput
              value={form.car_gr_model_id || 0}
              onChange={(e) => set("car_gr_model_id", Number(e.target.value) || 0)}
              placeholder="0 = auto"
              data-testid="cargr-model-id-input"
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function ReadOnly({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">{label}</div>
      <div className={`text-sm mt-0.5 truncate ${mono ? "font-mono" : ""}`} title={String(value)}>
        {value || "—"}
      </div>
    </div>
  );
}
